<template>
    <v-card class="modern-calendar" :style="cardStyles">
        <!-- Header del calendario -->
        <div class="calendar-header" :style="headerStyles">
            <v-btn 
                icon 
                @click="changeMonth(-1)" 
                :style="buttonStyles"
                class="nav-button"
            >
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            
            <div class="header-content">
                <div class="month-year-title">{{ currentMonthYear }}</div>
                <div class="header-actions">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                :color="currentTheme.success"
                                @click="SendMail()"
                                small
                                rounded
                                dark
                                class="mail-button"
                            >
                                <v-icon>mdi-gmail</v-icon>
                                <span class="ml-2">Enviar</span>
                            </v-btn>
                        </template>
                        <span>Enviar Vacaciones</span>
                    </v-tooltip>
                    
                    <v-btn
                        icon
                        @click="showThemeSelector = !showThemeSelector"
                        :style="buttonStyles"
                        class="theme-button"
                    >
                        <v-icon>mdi-palette</v-icon>
                    </v-btn>
                </div>
            </div>
            
            <v-btn 
                icon 
                @click="changeMonth(1)" 
                :style="buttonStyles"
                class="nav-button"
            >
                <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
        </div>

        <!-- Selector de temas -->
        <v-expand-transition>
            <div v-if="showThemeSelector" class="theme-selector" :style="themeSelectorStyles">
                <div class="theme-title">Selecciona un tema:</div>
                <div class="theme-options">
                    <div 
                        v-for="theme in themeOptions" 
                        :key="theme.name"
                        @click="selectTheme(theme)"
                        class="theme-option"
                        :class="{ active: selectedTheme === theme.name }"
                        :style="getThemeOptionStyles(theme)"
                    >
                        <div class="theme-preview">
                            <div class="color-circle" :style="{ backgroundColor: theme.primary }"></div>
                            <div class="color-circle" :style="{ backgroundColor: theme.secondary }"></div>
                            <div class="color-circle" :style="{ backgroundColor: theme.accent }"></div>
                        </div>
                        <span class="theme-name">{{ theme.displayName }}</span>
                    </div>
                </div>
            </div>
        </v-expand-transition>

        <!-- Calendario -->
        <div class="calendar-container">
            <!-- Días de la semana -->
            <div class="calendar-weekdays">
                <div v-for="day in weekdays" :key="day" class="weekday" :style="weekdayStyles">
                    {{ day }}
                </div>
            </div>

            <!-- Grid de días -->
            <div class="calendar-grid" ref="calendarGrid">
                <div 
                    v-for="(day, index) in calendarDays" 
                    :key="index"
                    :class="getDayClasses(day)"
                    :style="getDayStyles(day)"
                    @click="selectDay(day)"
                    @dblclick="dobleClick"
                >
                    <div class="day-number">{{ day.date }}</div>
                    <div v-if="day.events.length > 0" class="day-events">
                        <div 
                            v-for="(event, eventIndex) in day.events.slice(0, 2)" 
                            :key="eventIndex" 
                            class="event-tag"
                            :style="getEventTagStyles(event.title)"
                        >
                            {{ event.title }}
                        </div>
                        <div v-if="day.events.length > 2" class="event-tag more-events">
                            +{{ day.events.length - 2 }} más
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Dialog de detalle -->
        <v-dialog v-model="dialogDetailEvent" persistent max-width="900">
            <v-card>
                <v-card-title class="dialog-header" :style="headerStyles">
                    <span>Detalle de Vacaciones</span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="dialogDetailEvent = false" :style="buttonStyles">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-data-table
                        :headers="headers"
                        :items="itemsDetail"
                        class="modern-table"
                        :header-props="{ 
                            style: `background-color: ${currentTheme.primary}20; color: ${currentTheme.primary}` 
                        }"
                    >
                        <template v-slot:[`item.CtrAdmissionDate`]="{ item }">
                            {{ $fun.formatDateView(item.CtrAdmissionDate) }}
                        </template>
                        <template v-slot:[`item.VcdDateInit`]="{ item }">
                            {{ $fun.formatDateView(item.VcdDateInit) }}
                        </template>
                        <template v-slot:[`item.VcdDateEnd`]="{ item }">
                            {{ $fun.formatDateView(item.VcdDateEnd) }}
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import ServiceVacations from "../../services/HumanResource/HmnVacationsService";

export default {
    data() {
        return {
            calendarWidth: 0,
            currentDate: new Date(),
            selectedDate: null,
            selectDate: null,
            firstFormattedDateBackup: null,
            lastFormattedDateBackup: null,
            firstDayOfMonthFormatted: null,
            yearOfMonth: null,
            lastDayOfMonthFormatted: null,
            events: [],
            dialogDetailEvent: false,
            itemsDetail: [],
            showThemeSelector: false,
            selectedTheme: 'blue',
            weekdays: ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'],
            headers: [
                { text: "NOMBRE", value: "NtpFullName" },
                { text: "DNI", value: "PrsDocumentNumber" },
                { text: "TIPO DE PLANILLA", value: "TypePayrollName" },
                { text: "FECHA DE INGRESO", value: "CtrAdmissionDate" },
                { text: "FECHA DE INICIO", value: "VcdDateInit" },
                { text: "FECHA DE FIN", value: "VcdDateEnd" },
            ],
            themeOptions: [
                {
                    name: 'blue',
                    displayName: 'Azul Corporativo',
                    primary: '#1976D2',
                    secondary: '#42A5F5',
                    accent: '#FFC107',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#2196F3'
                },
                {
                    name: 'purple',
                    displayName: 'Púrpura Moderno',
                    primary: '#7B1FA2',
                    secondary: '#AB47BC',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#9C27B0'
                },
                {
                    name: 'green',
                    displayName: 'Verde Natural',
                    primary: '#388E3C',
                    secondary: '#66BB6A',
                    accent: '#FF5722',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'orange',
                    displayName: 'Naranja Energético',
                    primary: '#F57C00',
                    secondary: '#FFB74D',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#FF9800'
                },
                {
                    name: 'dark',
                    displayName: 'Oscuro Elegante',
                    primary: '#424242',
                    secondary: '#616161',
                    accent: '#00BCD4',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'pinkSoft',
                    displayName: 'Rosado Pastel',
                    primary: '#F48FB1',
                    secondary: '#FCE4EC',
                    accent: '#EC407A',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#F48FB1'
                }
            ]
        }
    },
    computed: {
        currentTheme() {
            return this.themeOptions.find(theme => theme.name === this.selectedTheme) || this.themeOptions[0];
        },
        cardStyles() {
            return {
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            };
        },
        headerStyles() {
            return {
                background: `linear-gradient(135deg, ${this.currentTheme.primary} 0%, ${this.currentTheme.secondary} 100%)`,
                color: 'white'
            };
        },
        buttonStyles() {
            return {
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)'
            };
        },
        weekdayStyles() {
            return {
                backgroundColor: `${this.currentTheme.primary}15`,
                color: this.currentTheme.primary,
                fontWeight: '600'
            };
        },
        themeSelectorStyles() {
            return {
                backgroundColor: `${this.currentTheme.primary}08`,
                borderBottom: `3px solid ${this.currentTheme.primary}20`
            };
        },
        currentMonthYear() {
            return this.currentDate.toLocaleString('es', { month: 'long', year: 'numeric' })
                .replace(/^\w/, c => c.toUpperCase())
        },
        calendarDays() {
            const year = this.currentDate.getFullYear()
            const month = this.currentDate.getMonth()
            const today = new Date()

            const firstDay = new Date(year, month, 1)
            const lastDay = new Date(year, month + 1, 0)
            const startingDay = firstDay.getDay()

            const days = []

            // Días del mes anterior
            for (let i = 0; i < startingDay; i++) {
                const prevMonthDay = new Date(year, month, -startingDay + i + 1)
                days.push({
                    date: prevMonthDay.getDate(),
                    isOtherMonth: true,
                    isToday: this.isSameDay(prevMonthDay, today),
                    events: this.getEventsForDate(prevMonthDay),
                    fullDate: prevMonthDay
                })
            }

            // Días del mes actual
            for (let i = 1; i <= lastDay.getDate(); i++) {
                const currentDay = new Date(year, month, i)
                const dayEvents = this.getEventsForDate(currentDay)

                days.push({
                    date: i,
                    isOtherMonth: false,
                    isToday: this.isSameDay(currentDay, today),
                    events: dayEvents,
                    fullDate: currentDay
                })
            }

            // Días del mes siguiente
            const totalDays = days.length
            const remainingDays = 42 - totalDays

            for (let i = 1; i <= remainingDays; i++) {
                const nextMonthDay = new Date(year, month + 1, i)
                days.push({
                    date: i,
                    isOtherMonth: true,
                    isToday: this.isSameDay(nextMonthDay, today),
                    events: this.getEventsForDate(nextMonthDay),
                    fullDate: nextMonthDay
                })
            }

            const firstFormattedDate = days[0].fullDate.toISOString().split('T')[0];
            const lastFormattedDate = days[days.length - 1].fullDate.toISOString().split('T')[0];

            this.firstDayOfMonthFormatted = firstDay.toISOString().split('T')[0];
            this.lastDayOfMonthFormatted = lastDay.toISOString().split('T')[0];
            this.yearOfMonth = firstDay.getFullYear();

            if(this.firstFormattedDateBackup != firstFormattedDate && this.lastFormattedDateBackup != lastFormattedDate){
                this.firstFormattedDateBackup = firstFormattedDate;
                this.lastFormattedDateBackup = lastFormattedDate;
                const PstID = this.$fun.getUserInfo().CtrContract?.PstID ? this.$fun.getUserInfo().CtrContract?.PstID : 0;
                ServiceVacations.listEvent(firstFormattedDate, lastFormattedDate, this.$fun.isAdmin() ? 1 : this.$fun.getUserID(), PstID, this.$fun.getUserID()).then((res) => {
                    if (res.status == 200){
                        if(res.data.length > 0){
                            const transformedData = this.generateDateArray(res.data);
                            this.events = transformedData;
                        }
                    }
                })
            }

            return days
        },
        dayWidth() {
            return this.calendarWidth ? `${this.calendarWidth / 7}px` : 'auto';
        }
    },
    methods: {
        // Métodos de tema
        selectTheme(theme) {
            this.selectedTheme = theme.name;
            this.saveThemeToStorage();
            this.showThemeSelector = false;
        },
        saveThemeToStorage() {
            localStorage.setItem('calendar-theme', this.selectedTheme);
        },
        loadThemeFromStorage() {
            const savedTheme = localStorage.getItem('calendar-theme');
            if (savedTheme) {
                this.selectedTheme = savedTheme;
            }
        },
        getThemeOptionStyles(theme) {
            const isActive = this.selectedTheme === theme.name;
            return {
                border: isActive ? `3px solid ${theme.primary}` : '2px solid transparent',
                backgroundColor: isActive ? `${theme.primary}10` : 'white',
                transform: isActive ? 'scale(1.05)' : 'scale(1)'
            };
        },
        
        // Métodos de estilo para días
        getDayClasses(day) {
            return [
                'calendar-day',
                { 'other-month': day.isOtherMonth },
                { 'has-events': day.events.length > 0 },
                { 'today': day.isToday },
                { 'selected': this.isSelectedDay(day) }
            ];
        },
        getDayStyles(day) {
            let styles = {
                border: `1px solid ${this.currentTheme.primary}20`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            };

            if (day.isOtherMonth) {
                styles.backgroundColor = '#f5f5f5';
                styles.color = '#999';
                styles.opacity = '0.6';
            } else if (day.isToday) {
                styles.background = `linear-gradient(135deg, ${this.currentTheme.primary} 0%, ${this.currentTheme.secondary} 100%)`;
                styles.color = 'white';
                styles.boxShadow = `0 4px 16px ${this.currentTheme.primary}40`;
            } else if (this.isSelectedDay(day)) {
                styles.border = `2px solid ${this.currentTheme.primary}`;
                styles.backgroundColor = `${this.currentTheme.primary}10`;
                styles.color = this.currentTheme.primary;
                styles.transform = 'scale(1.02)';
            }

            return styles;
        },
        getEventTagStyles(title) {
            const color = this.getEventColor(title);
            return {
                backgroundColor: color,
                color: 'white',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            };
        },

        // Métodos originales del calendario
        generateDateArray(data) {
            let result = [];
            let groupedData = [];
            let processedTitles = new Set();
            
            data.forEach(item => {
                if (!processedTitles.has(item.NtpFullName)) {
                    let sameTitleItems = data.filter(d => d.NtpFullName === item.NtpFullName);
                    groupedData.push(...sameTitleItems);
                    processedTitles.add(item.NtpFullName);
                }
            });
            
            groupedData.forEach(item => {
                let startDate = new Date(item.VcdDateInit);
                let endDate = new Date(item.VcdDateEnd);
                
                while (startDate <= endDate) {
                    result.push({
                        title: item.NtpFullName,
                        date: startDate.toISOString().split('T')[0]
                    });
                    startDate.setDate(startDate.getDate() + 1);
                }
            });
            
            return result;
        },
        getEventsForDate(date) {
            return this.events.filter(event => {
                const eventDate = new Date(event.date + "T00:00:00");
                return eventDate.getFullYear() === date.getFullYear() &&
                    eventDate.getMonth() === date.getMonth() &&
                    eventDate.getDate() === date.getDate()
            })
        },
        changeMonth(delta) {
            this.currentDate = new Date(
                this.currentDate.getFullYear(),
                this.currentDate.getMonth() + delta,
                1
            )
            this.selectedDate = null;
        },
        isSameDay(date1, date2) {
            return date1.getFullYear() === date2.getFullYear() &&
                date1.getMonth() === date2.getMonth() &&
                date1.getDate() === date2.getDate()
        },
        selectDay(day) {
            if (day.isOtherMonth) {
                const newDate = new Date(day.fullDate)
                this.currentDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1)
            }

            this.selectedDate = day.fullDate
            this.selectDate = this.selectedDate
                ? this.selectedDate.toISOString().split('T')[0]
                : null
        },
        dobleClick() {
            if (this.selectDate) {
                this.dialogDetailEvent = true;

                const PstID = this.$fun.getUserInfo().CtrContract?.PstID ? this.$fun.getUserInfo().CtrContract?.PstID : 0;
                ServiceVacations.listEvent(this.selectDate, this.selectDate, this.$fun.isAdmin() ? 1 : this.$fun.getUserID(), PstID, this.$fun.getUserID()).then((res) => {
                    if (res.status == 200){
                        if(res.data.length > 0){
                            this.itemsDetail = res.data;
                        }
                    }
                })
            }
        },
        isSelectedDay(day) {
            return this.selectedDate && this.isSameDay(this.selectedDate, day.fullDate)
        },
        updateCalendarWidth() {
            this.$nextTick(() => {
                const grid = this.$refs.calendarGrid;
                if (grid) {
                    this.calendarWidth = grid.clientWidth;
                }
            });
        },
        getEventColor(title) {
            let hash = 0;
            for (let i = 0; i < title.length; i++) {
                hash = title.charCodeAt(i) + ((hash << 5) - hash);
            }
            
            const hue = (hash * 37) % 360;
            const saturation = 65 + (hash % 20);
            const lightness = 45 + (hash % 15);
            
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        },
        SendMail(){
            const filter = {
                VcdBeginDate: this.firstDayOfMonthFormatted,
                VcdEndDate: this.lastDayOfMonthFormatted,
                VcdYear: this.yearOfMonth,
                Affair: this.currentMonthYear,
                EmailBody: `<html><body><p>Estimado (a)</p><p>Mediante el presente, se adjunta reporte de vacaciones del ${this.$fun.formatDateView(this.firstDayOfMonthFormatted)} al ${this.$fun.formatDateView(this.lastDayOfMonthFormatted)}.</p></body></html>`
            }

            this.$fun.alert("¿Estás seguro de enviar el correo?", "question").then((r) => {
                if (r.value) {
                    ServiceVacations.SendMail(filter, this.$fun.getUserID())
                    .then((res) => {
                        if (res.status == 200){
                            this.$fun.alert("Correo Enviado","success");
                        }
                    });
                }
            })
        }
    },
    watch: {
        events: {
            deep: true,
            handler() {
                this.$forceUpdate()
            }
        },
    },
    mounted() {
        this.loadThemeFromStorage();
        this.updateCalendarWidth();
        window.addEventListener('resize', this.updateCalendarWidth);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateCalendarWidth);
    }
}
</script>

<style scoped>
.modern-calendar {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    max-width: 100%;
    margin: 0 auto;
}

/* Header */
.calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    position: relative;
    overflow: hidden;
}

.calendar-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
}

.header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
}

.month-year-title {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 8px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.nav-button, .theme-button {
    border-radius: 50%;
    width: 48px;
    height: 48px;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
}

.nav-button:hover, .theme-button:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
}

.mail-button {
    text-transform: none;
    font-weight: 600;
    padding: 8px 16px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    transition: all 0.3s ease;
}

.mail-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.2);
}

/* Selector de temas */
.theme-selector {
    padding: 24px;
    animation: slideDown 0.3s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.theme-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
}

.theme-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.theme-option {
    display: flex;
    align-items: center;
    padding: 16px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.theme-option:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.theme-preview {
    display: flex;
    gap: 4px;
    margin-right: 12px;
}

.color-circle {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.theme-name {
    font-weight: 500;
    color: #333;
}

/* Calendario */
.calendar-container {
    padding: 24px;
}

.calendar-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
    margin-bottom: 16px;
}

.weekday {
    text-align: center;
    padding: 12px 8px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
    user-select: none;
}

.calendar-day {
    min-height: 120px;
    padding: 12px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    position: relative;
    background: white;
    overflow: hidden;
}

.calendar-day:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}

.calendar-day.other-month:hover {
    transform: none;
    box-shadow: none;
}

.day-number {
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 8px;
    line-height: 1;
}

.day-events {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.event-tag {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
    transition: all 0.2s ease;
}

/* .event-tag:hover {
    transform: scale(1.02);
} */

.more-events {
    background-color: #f0f0f0 !important;
    color: #666 !important;
    border: 1px solid #ddd;
}

/* Dialog */
.dialog-header {
    border-radius: 0;
    padding: 20px 24px;
}

.modern-table {
    border-radius: 8px;
    overflow: hidden;
}

/* Responsive */
@media (max-width: 768px) {
    .calendar-header {
        padding: 16px;
    }
    
    .month-year-title {
        font-size: 24px;
    }
    
    .header-actions {
        flex-direction: column;
        gap: 8px;
    }
    
    .calendar-container {
        padding: 16px;
    }
    
    .calendar-day {
        min-height: 80px;
        padding: 8px;
    }
    
    .day-number {
        font-size: 14px;
    }
    
    .event-tag {
        font-size: 10px;
        padding: 2px 6px;
    }
    
    .theme-options {
        grid-template-columns: 1fr;
    }
    
    .theme-option {
        padding: 12px;
    }
    
    .weekday {
        padding: 8px 4px;
        font-size: 12px;
    }
}

@media (max-width: 480px) {
    .header-content {
        flex-direction: column;
        gap: 12px;
    }
    
    .month-year-title {
        font-size: 20px;
    }
    
    .calendar-day {
        min-height: 60px;
        padding: 6px;
    }
    
    .day-number {
        font-size: 12px;
    }
    
    .event-tag {
        font-size: 9px;
        padding: 1px 4px;
    }
}

/* Animaciones adicionales */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.calendar-day {
    animation: fadeIn 0.3s ease;
}

/* Efectos de hover mejorados */
.calendar-day.has-events::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.calendar-day.has-events:hover::before {
    opacity: 1;
}

/* Estilos para modo oscuro (opcional) */
.modern-calendar.dark-mode {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
    color: white;
}

.modern-calendar.dark-mode .calendar-day {
    background: #333;
    color: white;
}

.modern-calendar.dark-mode .weekday {
    background-color: rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.8);
}

/* Estilos para impresión */
@media print {
    .theme-selector,
    .header-actions,
    .nav-button,
    .theme-button {
        display: none !important;
    }
    
    .calendar-header {
        background: #f5f5f5 !important;
        color: #333 !important;
    }
    
    .calendar-day {
        border: 1px solid #ddd !important;
        background: white !important;
        box-shadow: none !important;
    }
}

/* Accesibilidad */
.calendar-day:focus {
    outline: 3px solid #4CAF50;
    outline-offset: 2px;
}

.theme-option:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
}

/* Transiciones suaves para cambios de tema */
.modern-calendar * {
    transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

/* Estilos para eventos con overflow */
.day-events {
    max-height: 60px;
    overflow-y: auto;
}

.day-events::-webkit-scrollbar {
    width: 3px;
}

.day-events::-webkit-scrollbar-track {
    background: transparent;
}

.day-events::-webkit-scrollbar-thumb {
    background: rgba(0,0,0,0.2);
    border-radius: 2px;
}

.day-events::-webkit-scrollbar-thumb:hover {
    background: rgba(0,0,0,0.3);
}
</style>